import { memo, useMemo } from "react";

import { getURL } from "@/lib/aws-amplify/storage";
import type { LangCode } from "@/lib/gql/__generated__/graphql";

import type * as FormModelType from "@/components/features/design/plain/model/type";

import * as Shared from "./_shared";
import { ResourceCard } from "../../resource-card";
import { CardList } from "../card-list";

type Props = {
	plans: FormModelType.QueryData[ "planList" ][ "plans" ];
	primaryColor: string;
	fontFamilyClassName: string;
	styleColor: string;
	lang: LangCode;
};

export const PlanList = memo(({ plans, primaryColor: _primaryColor, fontFamilyClassName, styleColor, lang }: Props) => {
	const hasMore = useMemo(() => plans.length > 2, [ plans.length ]);
	const slicedPlans = useMemo(() => (hasMore ? plans.slice(0, 3) : plans), [ hasMore, plans ]);

	const convertedPlans = useMemo(() => {
		return slicedPlans.map((plan) => {
			const price = plan.price.amount;
			const isMoreThanZero = price > 0;
			const billingTypeTermWithTargetI18n = (Shared.Constants.BillingTypeTermWithTargetI18n[ plan.billingTypeTerm ][
				plan.billingTypeTarget
			] ?? { default: "", en: "" })[ lang ];
			return {
				title: plan.name,
				mainKeyVisualUrl: plan.mainKeyVisual ? getURL(plan.mainKeyVisual) : "",
				badge: plan.isDayTrip ? "日帰り" : undefined,
				catchCopy: plan.mainTarget ?? undefined,
				priceText: isMoreThanZero ? `+ ￥${plan.price.amount.toLocaleString()}` : "追加料金なし",
				priceUnit: isMoreThanZero ? billingTypeTermWithTargetI18n : undefined,
			};
		});
	}, [ lang, slicedPlans ]);

	return (
		<CardList
			title="プラン一覧"
			hasMore={hasMore}
			styleColor={styleColor}
			fontFamilyClassName={fontFamilyClassName}
		>
			<div className="flex flex-col gap-6">
				{convertedPlans.map((plan, index) => (
					<ResourceCard
						key={index}
						imageUrl={plan.mainKeyVisualUrl}
						title={plan.title}
						catchCopy={plan.catchCopy}
						badge={plan.badge}
						priceText={plan.priceText}
						priceUnit={plan.priceUnit}
					/>
				))}
			</div>
		</CardList>
	);
});
